import { AlertTriangle, BookOpen } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HandbookLibrary } from "@/pages/Handbooks";

const LESSONS = [
  ["What is cryptocurrency?", "Digital money that runs on a public ledger (a blockchain) instead of a bank. Bitcoin (BTC) was first; Ethereum (ETH) and Solana (SOL) also run apps."],
  ["What does the 24h change mean?", "How much the price moved compared with exactly 24 hours ago. +3% means it's 3% higher than yesterday at this time."],
  ["What is volume?", "The dollar value traded in the last 24 hours. High volume means lots of buying and selling; low volume moves can be less reliable."],
  ["What is market cap?", "Price × number of coins in circulation. It shows how big a coin is compared with others."],
  ["Momentum labels", "Positive Momentum: the 12-hour average is clearly above the 48-hour average and the last 24h were up. Negative Momentum: the opposite. Mixed Conditions: signals disagree or are too small. Insufficient Data: not enough clean prices to judge. A label only flips when the trend clearly crosses back, so it doesn't change every few minutes."],
  ["Volatility", "How much the price typically jumps around. We show the typical hourly move and the typical 24h range (1 standard deviation). High volatility = bigger swings both ways."],
  ["RSI", "Relative Strength Index (0–100). Above 70 often means a move is stretched; below 30 means selling is stretched. It's a hint, not a rule."],
  ["Polymarket BTC 15-minute markets", "A prediction market asking: will Bitcoin be higher or lower at the end of this 15-minute window than at the start? The price of 'Up' (e.g. 60¢) is roughly the crowd's chance (60%). If you're right, each contract pays $1."],
  ["Price alerts", "Pick a coin and a price. Our server checks every 60 seconds and marks the alert as triggered. While the app is open you'll get a pop-up."],
];

const RISKS = [
  "Crypto prices can fall a lot, fast. Never invest money you can't afford to lose.",
  "Past behaviour (trends, volatility, win rates) does not predict the future.",
  "Our insights are rule-based educational summaries — not financial advice and not guarantees.",
  "Data can be delayed or unavailable. We label stale data, but always double-check before acting.",
  "Prediction-market and trading rules, taxes and availability depend on where you live.",
];

export default function Learn() {
  return (
    <div className="space-y-5" data-testid="learn-page">
      <div><h1 className="font-head font-bold text-3xl tracking-tight flex items-center gap-2"><BookOpen className="w-7 h-7 text-[var(--cyan)]" />Learning center</h1>
        <p className="text-sm text-slate-400 mt-1">Short, plain-English guides. Two minutes each.</p></div>
      <HandbookLibrary />
      <h2 className="font-head font-semibold text-xl pt-2">Quick lessons</h2>
      <section className="glass p-2 sm:p-4">
        <Accordion type="single" collapsible>
          {LESSONS.map(([q, a], i) => (
            <AccordionItem key={q} value={q} className="border-[var(--line)]" data-testid={`lesson-${i}`}>
              <AccordionTrigger className="text-left px-3 hover:no-underline">{q}</AccordionTrigger>
              <AccordionContent className="px-3 text-slate-300 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
      <section className="glass p-5 border-amber-500/30" data-testid="risk-card">
        <h2 className="font-head font-semibold flex items-center gap-2 text-amber-200"><AlertTriangle className="w-5 h-5" />Risks you should know</h2>
        <ul className="mt-3 space-y-2 text-sm text-slate-300 list-disc pl-5">{RISKS.map((r) => <li key={r}>{r}</li>)}</ul>
      </section>
    </div>
  );
}
